import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Users, Search, Plus, Trash2, TrendingUp, DollarSign, 
  CreditCard, Smartphone, RefreshCw, X
} from 'lucide-react';
import { 
  listarTodosClientes, 
  excluirClienteAdmin, 
  obterEstatisticasLucro,
  cadastrarClienteManual
} from '../services/clientService';
import type { UsuarioFirestore } from '../types/firestore';

export function ClientsPanel() {
  const [clientes, setClientes] = useState<UsuarioFirestore[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [stats, setStats] = useState({ totalGeral: 0, totalDinheiro: 0, totalPix: 0, totalCartao: 0 });
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [novoCliente, setNovoCliente] = useState({ nome: '', empresa: '', whatsapp: '', cnpj_cpf: '' });
  const [saving, setSaving] = useState(false);

  const itemsPerPage = 20;

  const carregarDados = async () => {
    setLoading(true);
    try {
      const [clientesData, statsData] = await Promise.all([
        listarTodosClientes(),
        obterEstatisticasLucro()
      ]);
      setClientes(clientesData);
      setStats(statsData);
    } catch (error) {
      console.error('Erro ao carregar dados dos clientes:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const handleExcluir = async (uid: string) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return;
    try {
      await excluirClienteAdmin(uid);
      setClientes(prev => prev.filter(c => c.uid !== uid));
    } catch (error) {
      alert('Erro ao excluir cliente.');
    }
  };

  const handleCadastrar = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await cadastrarClienteManual(novoCliente);
      setIsModalOpen(false);
      setNovoCliente({ nome: '', empresa: '', whatsapp: '', cnpj_cpf: '' });
      await carregarDados(); // Recarrega para obter o novo cliente e atualizar estatísticas
    } catch (error) {
      alert('Erro ao cadastrar cliente.');
    } finally {
      setSaving(false);
    }
  };

  const clientesFiltradosEOrdenados = useMemo(() => {
    let filtrados = clientes.filter(c => {
      const termo = searchTerm.toLowerCase();
      return (
        c.nome.toLowerCase().includes(termo) ||
        (c.empresa && c.empresa.toLowerCase().includes(termo)) ||
        (c.cnpj_cpf && c.cnpj_cpf.includes(termo)) ||
        (c.whatsapp && c.whatsapp.includes(termo))
      );
    });

    // Ordenação alfabética
    return filtrados.sort((a, b) => a.nome.localeCompare(b.nome));
  }, [clientes, searchTerm]);

  const totalPages = Math.ceil(clientesFiltradosEOrdenados.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const clientesPaginados = clientesFiltradosEOrdenados.slice(startIndex, startIndex + itemsPerPage);

  // Reset page when filtering
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const formatarMoeda = (valor: number) => {
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
  };

  return (
    <div className="space-y-6">
      {/* Header Cards (Estatísticas) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-brand-blue/10 text-brand-blue flex items-center justify-center">
            <Users size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">Total Clientes</p>
            <h3 className="text-2xl font-black text-slate-800">{clientes.length}</h3>
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-emerald-500 to-emerald-700 p-5 rounded-3xl shadow-md text-white flex items-center gap-4 lg:col-span-1">
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center">
            <TrendingUp size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-emerald-100 uppercase">Lucro Geral</p>
            <h3 className="text-xl sm:text-2xl font-black">{formatarMoeda(stats.totalGeral)}</h3>
          </div>
        </div>



        <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-teal-500/10 text-teal-600 flex items-center justify-center">
            <Smartphone size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">PIX</p>
            <h3 className="text-xl font-black text-slate-800">{formatarMoeda(stats.totalPix)}</h3>
          </div>
        </div>

        <div className="bg-white p-5 rounded-3xl border border-slate-100 shadow-sm flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 text-indigo-600 flex items-center justify-center">
            <CreditCard size={24} />
          </div>
          <div>
            <p className="text-xs font-bold text-slate-500 uppercase">Cartão</p>
            <h3 className="text-xl font-black text-slate-800">{formatarMoeda(stats.totalCartao)}</h3>
          </div>
        </div>
      </div>

      {/* Tabela de Clientes */}
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-slate-100 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 text-white flex items-center justify-center">
              <Users size={18} />
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800">Clientes Cadastrados</h2>
              <p className="text-xs text-slate-500">Gerenciamento e listagem em ordem alfabética.</p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-64">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input 
                type="text" 
                placeholder="Buscar cliente..." 
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 transition-all"
              />
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="w-full sm:w-auto px-4 py-2 bg-brand-blue hover:bg-blue-700 text-white font-bold rounded-xl text-sm flex items-center justify-center gap-2 transition-colors shadow-md"
            >
              <Plus size={16} /> Novo Cliente
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-12 flex flex-col items-center justify-center text-slate-500">
            <RefreshCw size={32} className="animate-spin text-brand-blue mb-4" />
            <p className="font-medium animate-pulse">Carregando clientes...</p>
          </div>
        ) : clientesFiltradosEOrdenados.length === 0 ? (
          <div className="p-12 text-center text-slate-500">
            <Users size={48} className="mx-auto text-slate-300 mb-4" />
            <p className="font-bold text-lg">Nenhum cliente encontrado.</p>
            <p className="text-sm">Tente ajustar seus termos de busca.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="py-3 px-6 text-xs font-black text-slate-500 uppercase tracking-wider">Cliente</th>
                  <th className="py-3 px-6 text-xs font-black text-slate-500 uppercase tracking-wider">Contato / Empresa</th>
                  <th className="py-3 px-6 text-xs font-black text-slate-500 uppercase tracking-wider">Documento</th>
                  <th className="py-3 px-6 text-xs font-black text-slate-500 uppercase tracking-wider text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {clientesPaginados.map((cliente) => (
                  <tr key={cliente.uid} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                    <td className="py-4 px-6">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-brand-blue to-emerald-500 text-white flex items-center justify-center font-black text-sm shadow-sm">
                          {cliente.nome.substring(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">{cliente.nome}</p>
                          <p className="text-xs text-slate-400">UID: {cliente.uid.substring(0, 8)}...</p>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-6">
                      <p className="font-semibold text-slate-700">{cliente.whatsapp || '-'}</p>
                      <p className="text-xs text-slate-500 font-medium">{cliente.empresa || '-'}</p>
                    </td>
                    <td className="py-4 px-6">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200">
                        {cliente.cnpj_cpf || 'Não informado'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-right">
                      <button
                        onClick={() => handleExcluir(cliente.uid)}
                        className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Excluir Cliente"
                      >
                        <Trash2 size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Paginação */}
        {!loading && totalPages > 1 && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/50">
            <span className="text-xs font-medium text-slate-500">
              Mostrando {startIndex + 1} a {Math.min(startIndex + itemsPerPage, clientesFiltradosEOrdenados.length)} de {clientesFiltradosEOrdenados.length} clientes
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 font-medium text-sm disabled:opacity-50 hover:bg-slate-50"
              >
                Anterior
              </button>
              <span className="text-sm font-bold text-slate-700">
                {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 rounded-lg bg-white border border-slate-200 text-slate-600 font-medium text-sm disabled:opacity-50 hover:bg-slate-50"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal Cadastro */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[999] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm">
            <motion.div 
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white rounded-3xl p-6 max-w-md w-full shadow-2xl border border-slate-100"
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-black text-slate-800 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-brand-blue/10 text-brand-blue flex items-center justify-center">
                    <Plus size={18} />
                  </div>
                  Novo Cliente
                </h3>
                <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-700">
                  <X size={20} />
                </button>
              </div>

              <form onSubmit={handleCadastrar} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Nome Completo *</label>
                  <input required type="text" value={novoCliente.nome} onChange={e => setNovoCliente({...novoCliente, nome: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">Empresa (Opcional)</label>
                  <input type="text" value={novoCliente.empresa} onChange={e => setNovoCliente({...novoCliente, empresa: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">WhatsApp</label>
                  <input type="tel" value={novoCliente.whatsapp} onChange={e => setNovoCliente({...novoCliente, whatsapp: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">CNPJ / CPF</label>
                  <input type="text" value={novoCliente.cnpj_cpf} onChange={e => setNovoCliente({...novoCliente, cnpj_cpf: e.target.value})} className="w-full p-3 rounded-xl border border-slate-200 bg-slate-50 outline-none focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20" />
                </div>

                <div className="pt-4 flex justify-end gap-3">
                  <button type="button" onClick={() => setIsModalOpen(false)} className="px-5 py-2.5 rounded-xl font-bold text-slate-600 hover:bg-slate-100 transition-colors">
                    Cancelar
                  </button>
                  <button type="submit" disabled={saving} className="px-5 py-2.5 rounded-xl font-bold text-white bg-brand-blue hover:bg-blue-700 transition-colors shadow-md disabled:opacity-70">
                    {saving ? 'Salvando...' : 'Salvar Cliente'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
